<template>
  <a
    href="https://m.me/TaybaTravelBE"
    target="_blank"
    rel="noopener noreferrer"
    class="fixed z-50 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 hover:scale-110"
    :class="[sizeClasses, visibilityClasses, compactClasses]"
    :style="[
      positionStyle,
      {
        background:
          'radial-gradient(circle at 30% 110%, #0084ff 0%, #a033ff 60%, #ff5c87 100%)',
      },
    ]"
    aria-label="Contactez-nous sur Messenger"
  >
    <!-- Official Messenger lightning bolt icon -->
    <svg
      class="text-white"
      :class="iconSizeClass"
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12 2C6.477 2 2 6.145 2 11.259c0 2.821 1.323 5.337 3.405 7.04V21l3.113-1.708A10.985 10.985 0 0012 19.517c5.524 0 10-4.145 10-9.258C22 6.145 17.523 2 12 2zm1.388 12.459l-2.545-2.71-4.97 2.71 5.469-5.802 2.608 2.71 4.906-2.71-5.468 5.802z"
      />
    </svg>
  </a>
</template>

<script setup lang="ts">
interface Props {
  size?: "sm" | "md" | "lg";
  right?: string;
  bottom?: string;
}

const props = withDefaults(defineProps<Props>(), {
  size: "lg",
  right: "10px",
  bottom: "105px",
});

const sizeClasses = computed(() => {
  switch (props.size) {
    case "sm":
      return "w-12 h-12";
    case "md":
      return "w-14 h-14";
    case "lg":
    default:
      return "w-[42px] h-[42px] sm:w-[62px] sm:h-[62px]";
  }
});

const iconSizeClass = computed(() => {
  switch (props.size) {
    case "sm":
      return "w-6 h-6";
    case "md":
      return "w-7 h-7";
    case "lg":
    default:
      return "w-6 h-6 sm:w-9 sm:h-9";
  }
});

const isVisible = ref(false);
const isCompact = ref(false);
const isMobile = ref(false);
const isFooterVisible = ref(false);

const visibilityClasses = computed(() => {
  // On mobile, hide when the footer scrolls into view to avoid covering links.
  if (isMobile.value && isFooterVisible.value)
    return "opacity-0 pointer-events-none translate-y-2";
  return isVisible.value
    ? "opacity-100 translate-y-0"
    : "opacity-0 translate-y-2";
});

const compactClasses = computed(() => (isCompact.value ? "scale-90" : ""));

const positionStyle = computed(() => {
  if (isMobile.value) {
    return {
      left: "16px",
      right: "auto",
      bottom: `calc(80px + env(safe-area-inset-bottom))`,
    };
  }
  return {
    right: props.right,
    left: "auto",
    bottom: `calc(${props.bottom} + env(safe-area-inset-bottom))`,
  };
});

let appearTimeout: ReturnType<typeof setTimeout> | null = null;
let footerObserver: IntersectionObserver | null = null;

const handleScroll = () => {
  isCompact.value = window.scrollY > 120;
};

const checkMobile = () => {
  isMobile.value = window.innerWidth < 640;
};

onMounted(() => {
  checkMobile();
  window.addEventListener("resize", checkMobile, { passive: true });

  appearTimeout = setTimeout(() => {
    isVisible.value = true;
  }, 1400);

  handleScroll();
  window.addEventListener("scroll", handleScroll, { passive: true });

  // Hide on mobile when footer enters the viewport
  const footer = document.querySelector("footer");
  if (footer) {
    footerObserver = new IntersectionObserver(
      ([entry]) => {
        isFooterVisible.value = entry?.isIntersecting ?? false;
      },
      { threshold: 0 },
    );
    footerObserver.observe(footer);
  }
});

onUnmounted(() => {
  if (appearTimeout) clearTimeout(appearTimeout);
  window.removeEventListener("scroll", handleScroll);
  window.removeEventListener("resize", checkMobile);
  footerObserver?.disconnect();
});
</script>
